import classNames from 'classnames';

import { filterKeyPress } from '~globals/common';

type Props = {
    errorValue: { zip: string; county: string };
    redesign?: boolean;
    setZipValue: (a: string) => void;
    setCountyValue: (a: string) => void;
    shouldDisplayErrors?: boolean;
    zipValue: string;
    countyValue: string;
    multiCountyList: null | GeoLocation[];
};

const ZipInput = ({
    shouldDisplayErrors,
    errorValue,
    zipValue,
    countyValue,
    setZipValue,
    setCountyValue,
    multiCountyList,
    redesign,
}: Props) => {
    const zipId = 'zipCode';
    const countyId = 'countySelect';

    const getInputClasses = (err: string) => {
        return classNames(
            `w-full ${
                redesign ? 'xl:w-32' : 'md:w-32'
            } p-3 rounded placeholder-gray text-gray-dark h-[50px]`,
            shouldDisplayErrors && err
                ? 'border-2 border-red bg-red-lightest'
                : 'border border-gray bg-white'
        );
    };

    return (
        <div
            className={classNames(
                'lg:mr-0 lg:flex-row',
                redesign ? 'flex' : 'md:flex md:flex-col md:mr-4'
            )}
        >
            <div
                className={classNames(
                    'flex flex-col mb-4',
                    redesign ? 'w-full md:mb-0 xl:mr-4' : 'md:mb-2 lg:mr-4 lg:mb-0',
                    redesign && multiCountyList && 'mr-4 md:mr-6 lg:mr-4'
                )}
            >
                <label htmlFor={zipId} className="text-base" data-content="ZIP code">
                    ZIP Code
                </label>
                <input
                    className={getInputClasses(errorValue.zip)}
                    id={zipId}
                    autoComplete="postal-code"
                    data-qa="zip"
                    type="text"
                    maxLength={5}
                    aria-required="true"
                    value={zipValue}
                    onKeyPress={filterKeyPress}
                    onChange={(e) => setZipValue(e.target.value)}
                />
                <div
                    className={classNames(
                        'flex items-center md:items-start mt-1 text-sm text-red',
                        shouldDisplayErrors && errorValue.zip ? '' : 'hidden'
                    )}
                >
                    <i className="icon-error h-4 w-4 mr-1 md:mt-1" data-qa="error-zip-icon"></i>
                    <span
                        className={`${zipId}-error-text inline-block md:max-w-[105px]`}
                        data-qa="error-zip"
                        aria-live="polite"
                    >
                        {errorValue.zip}
                    </span>
                </div>
            </div>

            <div
                className={
                    multiCountyList
                        ? `flex flex-col mb-4 md:mb-0 ${redesign ? 'w-full xl:mr-4' : 'md:mr-4'}`
                        : 'hidden'
                }
            >
                <label htmlFor={countyId} className="text-base" data-content="County">
                    County
                </label>
                <select
                    id={countyId}
                    className={getInputClasses(errorValue.county)}
                    onChange={(e) => setCountyValue(e.target.value)}
                    defaultValue={countyValue}
                >
                    <option value="" disabled>
                        Select...
                    </option>
                    {multiCountyList?.map((location) => {
                        return (
                            <option
                                key={`${location.ssacd as string}_${location.countyName}`}
                                value={location.countyName}
                            >
                                {location.countyName}
                            </option>
                        );
                    })}
                </select>
                <div
                    className={classNames(
                        'flex items-center md:items-start mt-1 text-sm text-red',
                        shouldDisplayErrors && errorValue.county ? '' : 'hidden'
                    )}
                >
                    <i className="icon-error h-4 w-4 mr-1 md:mt-1" data-qa="error-county-icon"></i>
                    <span
                        className={`${countyId}-error-text inline-block md:max-w-[105px]`}
                        data-qa="error-county"
                        aria-live="polite"
                    >
                        {errorValue.county}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ZipInput;
